import { styled } from '@mui/material/styles';
import { Drawer } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import { Link } from 'react-router-dom';
import List from '@mui/material/List';
import Switch from '@mui/material/Switch';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import COLORS from '@styles/theme/colors';

const expandDrawerWidth = 250;
const collapseDrawerWidth = 70;

export const SideBar = styled(Drawer)(({ theme, open }) => ({
  width: expandDrawerWidth,
  whiteSpace: 'nowrap',

  '& .MuiDrawer-paper': {
    boxShadow: '0px 2px 4px rgba(51, 70, 129, 0.1), inset 0px -1px 0px #E8ECEF',
    transition: theme.transitions.create('width'),
    overflowX: 'hidden',
    paddingBottom: 80,
    width: open ? expandDrawerWidth : collapseDrawerWidth,
    borderRight: 'none',
  }
}));

export const StyledList = styled(List)({
  paddingTop: 20,
  margin: '0 14px 10px',
  paddingBottom: 0
});

export const IconWrap = styled('div')(({ theme }) => ({
  borderRadius: 6,
  display: 'flex',
  background: 'transparent',
  color: theme.palette.text.secondary,
  width: 43,
  height: 43,
  fontSize: 18,
  flex: '0 0 auto',
  marginRight: 12,

  '& > *': {
    margin: 'auto'
  }
}));

export const StyledListItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer',
  padding: '0 15px 0 0',
  transition: 'all .3s ease',
  borderRadius: 6,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '& + &': {
    marginTop: 2
  },

  '& span': {
    fontSize: 16,
    fontWeight: 600
  },

  '& .icon': {
    transition: 'all .3s ease'
  },

  '&:hover': {
    color: COLORS.accent,

    '& .icon': {
      color: COLORS.accent
    }
  },

  '&.active': {
    backgroundColor: COLORS.accent,

    '& .icon': {
      background: theme.palette.primary.main,
      boxShadow: '0px 4px 10px rgba(56, 125, 255, 0.24)',
      color: COLORS.white,
      transition: 'all .3s ease'
    },

    '& .MuiListItemText-primary': {
      color: COLORS.white,
      transition: 'all .3s ease'
    }
  }
}));

export const StyledLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  color: 'inherit',
  textDecoration: 'none'
});

export const AccordionWrap = styled(Accordion)({
  boxShadow: 'none',
  minHeight: 'auto',
  margin: '0 14px',
  display: 'flex',
  flexDirection: 'column',

  '& .MuiAccordionSummary-expandIconWrapper': {
    marginRight: 5
  },

  '&.Mui-expanded, &.active': {
    minHeight: 'auto',
    margin: '0 14px',

    '& .MuiAccordionSummary-root': {
      minHeight: 'auto',
      margin: 0,

      '& span': {
        color: COLORS.accent
      },

      '& .icon': {
        color: COLORS.accent
      }
    }
  },

  '&.active': {
    '& .MuiAccordionSummary-expandIconWrapper .icon': {
      color: 'white'
    },


    '& .MuiAccordionSummary-root': {
      backgroundColor: COLORS.accent,

      '& .accordion-header': {
        '& span': {
          color: COLORS.white
        },

        '& .icon': {
          color: COLORS.white
        }
      }
    },

  },

  '&:before': {
    display: 'none'
  }
});

export const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  border: 'none',
  cursor: 'pointer',
  fontSize: 16,
  minHeight: 'auto',
  borderRadius: 6,
  padding: 0,
  minWidth: '100%',

  '&:hover': {
    color: COLORS.accent,

    '& span': {
      color: COLORS.accent
    },

    '& .icon': {
      color: COLORS.accent
    }
  },

  '&.Mui-expanded': {
    '& .MuiAccordionSummary-expandIconWrapper .icon': {
      color: COLORS.accent
    },

    '& .accordion-header': {
      '& span': {
        color: COLORS.accent
      },

      '& .icon': {
        color: COLORS.accent
      }
    }

  },

  '&.active': {
    minHeight: 'auto',
    margin: 0,
    backgroundColor: COLORS.accent,

    '& span': {
      color: COLORS.white
    },

    '& .icon': {
      color: COLORS.white
    }
  },

  '& span': {
    fontSize: 16,
    color: theme.palette.text.secondary,
    fontWeight: 600
  },

  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: 0,
    width: '100%',

    '&.Mui-expanded, &.active': {
      minHeight: 'auto',
      margin: 0
    }
  },

  '& .accordion-header': {
    display: 'flex',
    alignItems: 'center'
  }
}));

export const AccordionText = styled(AccordionDetails)({
  '& .MuiList-padding': {
    padding: 0
  },

  '& .MuiListItem-root': {
    paddingLeft: 12,
    fontSize: 14,
    marginBottom: 7,
    marginLeft: 15,

    '& span': {
      fontSize: 16,
      fontWeight: 500
    },

    '&.active': {
      backgroundColor: 'transparent',

      '& *': {
        color: COLORS.accent,
        fontWeight: 600
      }
    },

    '&:hover': {
      background: 'transparent',
      color: COLORS.accent,

      '& *': {
        color: COLORS.accent
      }
    }
  }
});

export const SwitchWrap = styled('label')(({ theme }) =>({
  marginTop: 'auto',
  padding: '10px 20px',
  display: 'flex',
  cursor: 'pointer',
  alignItems: 'center',
  opacity: 1,
  color: theme.palette.text.primary,
}));

export const StyledSwitch = styled(Switch)(({ theme }) =>({
  flex: '0 0 auto',
  marginLeft: -12,
  marginRight: 3,

  '& .MuiSwitch-track': {
    background: 'transparent',
    opacity: 1,
    border: '1px solid #C1C9D9'
  },

  '& .Mui-checked + .MuiSwitch-track': {
    borderColor: theme.palette.primary.main,
  },

  '& .Mui-checked .MuiSwitch-thumb': {
    backgroundColor: theme.palette.primary.main,
    opacity: 1
  },

  '& .MuiSwitch-switchBase': {
    padding: 14,

    '&:hover': {
      background: 'none',
    }
  },

  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 10,
    height: 10,
    backgroundColor: theme.palette.primary.main,
  }
}));
